<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useMediaQuery } from "@vueuse/core";
import { useGlobalStore } from "@stores/global";
import IconX from "@components/icons/X.vue";
import IconInfoCircle from "@components/icons/InfoCircle.vue";
import TextInput from "@components/TextInput.vue";
import Button from "@components/Button.vue";

const globalStore = useGlobalStore();

const props = defineProps({
    data: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    errors: { type: Object, default: () => ({}) },
    countryPlaceholder: { type: String, default: "" },
});

const emit = defineEmits(["submit"]);

const country = defineModel("country", { type: String, default: "" });
const email = defineModel("email", { type: String, default: "" });
const agreed = defineModel("agreed", { type: Boolean, default: false });

const isMobile = useMediaQuery("(max-width: 1024px)");
const activeTab = ref("country");

const canSubmit = computed(() => country.value.trim() && email.value.trim());

// Countdown
const now = ref(Date.now());
let timer = null;

onMounted(() => {
    timer = setInterval(() => {
        now.value = Date.now();
    }, 1000);
});

onUnmounted(() => {
    clearInterval(timer);
});

const countdown = computed(() => {
    const end = new Date(props.data.ends_at).getTime();
    const diff = Math.max(0, end - now.value);

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hrs = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const min = Math.floor((diff / (1000 * 60)) % 60);
    const sec = Math.floor((diff / 1000) % 60);

    return [
        { value: String(days).padStart(2, "0"), label: "Days" },
        { value: String(hrs).padStart(2, "0"), label: "Hrs" },
        { value: String(min).padStart(2, "0"), label: "Min" },
        { value: String(sec).padStart(2, "0"), label: "Sec" },
    ];
});

const maxVotes = computed(() =>
    Math.max(...props.data.votes.map((v) => v.total), 1),
);

function close() {
    globalStore.setPage("explore");
}

function submit() {
    emit("submit", { country: country.value, email: email.value });
}
</script>

<template>
    <Transition name="panel" appear>
        <div
            class="vote-sidebar bg-marine-800 border-marine-500 flex flex-col overflow-y-auto border-b lg:border-l"
        >
            <div class="relative flex shrink-0 flex-col gap-2 p-6 max-lg:pt-6">
                <div class="flex items-center justify-between">
                    <p
                        class="h6 leading-[0.9] font-bold tracking-tight uppercase"
                    >
                        Vote
                    </p>
                    <button
                        class="border-marine-600 shadow-marine-600 flex size-8 shrink-0 items-center justify-center rounded-[4px] border-2 bg-yellow-400 shadow-[0_4px_0]"
                        @click="close"
                    >
                        <IconX class="text-marine-600 size-3" />
                    </button>
                </div>
            </div>

            <div
                class="border-marine-400 hidden border-b max-lg:flex max-lg:px-4"
            >
                <button
                    v-for="tab in [
                        { id: 'country', label: 'Select a country' },
                        { id: 'info', label: 'Information' },
                    ]"
                    :key="tab.id"
                    :class="[
                        'body-sm flex h-10 flex-1 items-center justify-center border-b-2 px-2 py-4 font-medium uppercase transition-colors',
                        activeTab === tab.id
                            ? 'border-yellow-400 text-yellow-400'
                            : 'faded-text border-transparent',
                    ]"
                    @click="activeTab = tab.id"
                >
                    {{ tab.label }}
                </button>
            </div>

            <div class="flex flex-col gap-6 px-6 pt-6 pb-6 lg:pt-0">
                <div
                    v-show="!isMobile || activeTab === 'info'"
                    class="flex flex-col gap-6"
                >
                    <p class="body-sm leading-[1.4]">
                        Vote for the country you want him to explore. The more
                        votes, the closer it gets to happening.
                    </p>

                    <div
                        v-if="loading"
                        class="flex items-center justify-center py-10"
                    >
                        <svg
                            class="size-6 animate-spin text-yellow-400"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-opacity="0.25"
                            />
                            <path
                                d="M22 12a10 10 0 0 0-10-10"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                            />
                        </svg>
                    </div>

                    <template v-else>
                        <div class="flex flex-col gap-3">
                            <p
                                class="body-sm faded-text leading-[0.9] font-bold uppercase"
                            >
                                Voting ends in
                            </p>
                            <div
                                class="border-marine-500 flex items-center justify-center border p-4"
                            >
                                <div class="flex w-full gap-2 uppercase">
                                    <div
                                        v-for="unit in countdown"
                                        :key="unit.label"
                                        class="flex flex-1 flex-col items-center gap-2"
                                    >
                                        <p
                                            class="h5 leading-[0.9] font-extrabold text-yellow-400"
                                        >
                                            {{ unit.value }}
                                        </p>
                                        <p
                                            class="body-sm faded-text leading-[1.2] font-medium"
                                        >
                                            {{ unit.label }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-col gap-3">
                            <div
                                v-for="(vote, i) in data.votes"
                                :key="vote.name"
                                class="flex flex-col gap-1"
                            >
                                <p
                                    class="body-sm leading-[0.8] font-bold uppercase"
                                >
                                    {{ vote.name }}
                                </p>
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-2">
                                        <div
                                            :class="[
                                                'h-3.5 rounded-[4px] border-2 shadow-[0_4px_0]',
                                                i === 0
                                                    ? 'border-marine-600 shadow-marine-600 bg-yellow-400'
                                                    : 'border-marine-700 shadow-marine-700 bg-marine-500',
                                            ]"
                                            :style="{
                                                width:
                                                    Math.max(
                                                        (vote.total /
                                                            maxVotes) *
                                                            160,
                                                        12,
                                                    ) + 'px',
                                            }"
                                        />
                                        <p
                                            :class="[
                                                'body-sm leading-[0.9] font-extrabold uppercase',
                                                i === 0
                                                    ? 'text-yellow-400'
                                                    : 'faded-text',
                                            ]"
                                        >
                                            {{ vote.percentage }}
                                        </p>
                                    </div>
                                    <p
                                        class="body-sm faded-text leading-[1.2] font-medium uppercase"
                                    >
                                        {{ vote.total }} votes
                                    </p>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <div
                    v-show="!isMobile || activeTab === 'country'"
                    class="flex flex-col gap-5 lg:mt-6"
                >
                    <TextInput
                        v-model="country"
                        label="Select a country"
                        :placeholder="countryPlaceholder"
                        :error="errors?.country?.[0]"
                        input-class="w-full"
                    />
                    <TextInput
                        v-model="email"
                        label="Enter your email"
                        placeholder="Your email"
                        :error="errors?.email?.[0]"
                        input-class="w-full"
                    />

                    <div class="flex items-center gap-2">
                        <IconInfoCircle class="size-4 shrink-0 opacity-50" />
                        <p class="body-sm faded-text leading-[1.2]">
                            We collect email to power site functionality
                        </p>
                    </div>

                    <label class="flex items-start gap-2">
                        <button
                            :class="[
                                'border-marine-500 bg-marine-800 flex size-5 shrink-0 items-center justify-center overflow-clip border',
                                agreed && 'border-yellow-400 bg-yellow-400',
                            ]"
                            @click.prevent="agreed = !agreed"
                        >
                            <svg
                                v-if="agreed"
                                width="12"
                                height="12"
                                viewBox="0 0 20 20"
                                fill="none"
                                class="text-marine-800"
                            >
                                <path
                                    d="M16.6668 5L7.50016 14.1667L3.3335 10"
                                    stroke="currentColor"
                                    stroke-width="3"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                        <span class="body-sm faded-text leading-[1.2]">
                            Receive the latest updates on Exspeedia.com,
                            campaign related material, as well as deals, offers
                            and other information from Expedia via email. You
                            can opt out of marketing emails anytime.
                        </span>
                    </label>

                    <Button :disabled="!canSubmit" @click="submit"
                        >Submit</Button
                    >
                </div>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.vote-sidebar {
    grid-column: -6 / -1;
    grid-row: 2 / -1;
    z-index: 2;
}

@media (max-width: 1024px) {
    .vote-sidebar {
        grid-column: 1 / -1;
        grid-row: -2 / -1;
        align-self: end;
        max-height: 50svh;
        height: auto;
    }
}
</style>
